import { CheckCircleOutlined, DatabaseOutlined } from '@ant-design/icons';
import { Badge, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { TenantFeature } from '@repo/contracts/common';

import { useGetDataCompletenessSummaryQuery } from '@/features/data-completeness/api/data-completeness.api';
import { useEntitlement } from '@/features/entitlements/hooks/useEntitlement';
import { useProgressRouter } from '@/hooks/use-progress-router';
import { useScope } from '@/hooks/use-scope';

interface DataCompletenessBadgeProps {
  isCollapsed: boolean;
}

export function DataCompletenessBadge({
  isCollapsed,
}: DataCompletenessBadgeProps) {
  const router = useProgressRouter();
  const { organizationSlug } = useScope();
  const { t } = useTranslation();
  const { hasFeature } = useEntitlement();

  const featureEnabled = hasFeature(TenantFeature.DOCUMENT_AUTO_GENERATION);

  const { data: summary } = useGetDataCompletenessSummaryQuery(undefined, {
    pollingInterval: 5 * 60 * 1000,
    skip: !organizationSlug || !featureEnabled,
  });

  // Only show in organization context with the right feature
  if (!organizationSlug || !featureEnabled) return null;

  const handleClick = () => {
    router.push(`/org/${organizationSlug}/data-completeness`);
  };

  const isComplete = summary?.totalMissing === 0;
  const missingCount = summary?.totalMissing ?? 0;

  const tooltipTitle = summary
    ? isComplete
      ? t('dataCompleteness.allComplete')
      : t('dataCompleteness.missingFields', { count: missingCount })
    : t('dataCompleteness.notComputed');

  return (
    <Tooltip title={tooltipTitle} placement={isCollapsed ? 'right' : undefined}>
      <button
        onClick={handleClick}
        className="inline-flex cursor-pointer justify-center rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
      >
        <Badge count={missingCount} size="small" offset={[-2, 2]}>
          <span className="text-2xl text-gray-500">
            {isComplete && summary ? (
              <CheckCircleOutlined style={{ color: '#16a34a' }} />
            ) : (
              <DatabaseOutlined />
            )}
          </span>
        </Badge>
      </button>
    </Tooltip>
  );
}
